import { forkJoin, of } from 'rxjs';
// Angular
import { Injectable } from '@angular/core';
// RxJS
import { map, mergeMap, catchError } from 'rxjs/operators';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// CRUD
import { QueryParamsModel } from '../../_base/crud';
// Services
import { PatientsService } from '../_services/patients.service';
// State
import { AppState } from '../../reducers';
// Actions
import {
  PatientActionToggleLoading,
  PatientActionTypes,
  PatientCreated,
  PatientOnServerCreated,
  PatientsPageLoaded,
  PatientsPageRequested,
  PatientsPageToggleLoading,
  PatientStatusUpdated,
  PatientsStatusUpdated,
  PatientUpdated,
  ManyPatientsDeleted,
  OnePatientDeleted,
  ThrowPatientError,
  ClearPatientErrors,
  PatientsStatusOnServerUpdated,
  PatientStatusOnServerUpdated,
  PatientOnServerUpdated,
  ManyPatientsOnServerDeleted,
  OnePatientOnServerDeleted,
  PatientsPageCancelled,
  FirePatientSuccess,
} from '../_actions/patient.actions';
import { Router } from '@angular/router';
import { FilteredViewService } from '../../services/filtered-view.service';

@Injectable()
export class PatientEffects {
  showPageLoadingDistpatcher = new PatientsPageToggleLoading({ isLoading: true });
  showActionLoadingDistpatcher = new PatientActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PatientActionToggleLoading({ isLoading: false });

  @Effect()
  loadPatientsPage$ = this.actions$.pipe(
    ofType<PatientsPageRequested>(PatientActionTypes.PatientsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.patientsService.findPatients(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PatientsPageCancelled();
            } else {
              this.store.dispatch(new ThrowPatientError({ error: response[0].error }));
              return new PatientsPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearPatientErrors());
            return new PatientsPageLoaded({
              patients: result.data,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatient$ = this.actions$.pipe(
    ofType<OnePatientOnServerDeleted>(PatientActionTypes.OnePatientOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.deletePatient(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new OnePatientDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatients$ = this.actions$.pipe(
    ofType<ManyPatientsOnServerDeleted>(PatientActionTypes.ManyPatientsOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.deletePatients(payload.ids).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new ManyPatientsDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatient$ = this.actions$.pipe(
    ofType<PatientOnServerUpdated>(PatientActionTypes.PatientOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updatePatient(payload.patient).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientStatus$ = this.actions$.pipe(
    ofType<PatientStatusOnServerUpdated>(PatientActionTypes.PatientStatusOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updateStatusForPatient(payload.id, payload.is_active).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientStatusUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientsStatus$ = this.actions$.pipe(
    ofType<PatientsStatusOnServerUpdated>(PatientActionTypes.PatientsStatusOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updateStatusForPatients(payload.ids, payload.is_active).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientsStatusUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createPatient$ = this.actions$.pipe(
    ofType<PatientOnServerCreated>(PatientActionTypes.PatientOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.createPatient(payload.patient).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientError({ error: res });
          } else {
            this.store.dispatch(new ClearPatientErrors());
            this.store.dispatch(
              new FirePatientSuccess({ action_type: PatientActionTypes.PatientCreated }),
            );
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            const accessObj = {
              entity_id: res.data.id,
              access_from: 'Patient List',
              entity_type: 'PATIENT',
            };
            this.sessionService.createRecentSession(accessObj).subscribe();
            return new PatientCreated({ patient: res.data });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private patientsService: PatientsService,
    private store: Store<AppState>,
    private sessionService: FilteredViewService,
    private router: Router,
  ) {}
}
